import { inject, Injectable } from "@angular/core";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse, HttpContextToken } from "@angular/common/http";
import { Observable, tap } from "rxjs";
import { Router } from "@angular/router";
import { ToastService } from "@kernel/tools/message/message.config";
import { isObject } from "@kernel/shared/helpers";
import { AuthServiceImpl } from "@app/shared/authentication/services/auth.servcice";
import { DISABLE_TOAST } from "@app/shared/general/tokens/disable-toast.token";
import { DISABLE_AUTH_HANDLE } from "@app/shared/general/tokens/disable-auth-handle.token";

/**
 * An HTTP interceptor that globally handles error responses for REST and GraphQL requests.
 * It supports automatic toast notifications for errors and can redirect to the login page on 401 errors.
 *
 * You can disable toast notifications or authorization handling per request using the `DISABLE_TOAST` and `DISABLE_AUTH_HANDLE` context tokens.
 *
 * Example of disabling toast notifications on a specific request:
 * ```ts
 * import { HttpContext } from '@angular/common/http';
 * import { DISABLE_TOAST } from './tokens/disable-toast.token';
 *
 * this.http.get('/api/silent', {
 *   context: new HttpContext().set(DISABLE_TOAST, true)
 * });
 * ```
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private authService = inject(AuthServiceImpl);
  private router = inject(Router);
  private toast = inject(ToastService);

  /**
   * Intercepts all outgoing HTTP requests and processes error responses.
   *
   * @param req - The outgoing HTTP request.
   * @param next - The HTTP request handler.
   * @returns An observable stream of HTTP events.
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const disableToastFn = this.getDisableToastFn(req);
    const disableAuthHandleFn = this.getDisableAuthHandleFn(req);

    return next.handle(req).pipe(
      tap({
        next: event => {
          // Handles GraphQL-style responses (arrays with errors)
          if (event instanceof HttpResponse && Array.isArray(event.body)) {
            const isUnauthorized = this.handleGraphQLErrors(event.body, disableToastFn, disableAuthHandleFn);
            if (isUnauthorized) {
              this.authService.clearUser();
              this.router.navigate(["/auth/login"]);
            }
          }
        },
        error: (response: HttpErrorResponse) => {
          // Handle REST API errors
          if (response.status === 401 && !disableAuthHandleFn(response.error)) {
            this.authService.clearUser();
            this.router.navigate(["/auth/login"]);
          }

          // Handle error toast notifications
          if (!disableToastFn(response.error)) {
            const messageKey = response.error?.data?.messageKey ?? response.error?.message ?? "ERRORS.SOMETHING_WENT_WRONG";
            this.toast.error(messageKey, "ERRORS.SUMMARY");
          }
        },
      })
    );
  }

  /**
   * Handles errors in GraphQL-style response arrays.
   *
   * @param responseItems - The array of GraphQL response objects containing error information.
   * @param disableToastFn - A function that determines whether toast notifications should be suppressed.
   * @param disableAuthHandleFn - A function that determines whether authorization handling should be suppressed.
   * @returns `true` if an unauthorized error (401) was found, otherwise `false`.
   */
  private handleGraphQLErrors(
    responseItems: unknown[],
    disableToastFn: (error: unknown) => boolean | null,
    disableAuthHandleFn: (error: unknown) => boolean | null
  ): boolean {
    let isUnauthorized = false;

    for (const item of responseItems) {
      const gqlErrors = isObject(item) && "errors" in item && item?.errors;
      if (!Array.isArray(gqlErrors)) continue;

      for (const error of gqlErrors) {
        const formatted = error?.extensions?.formatted ?? error?.extensions;
        const { code, type, message, data = error?.extensions } = formatted || {};

        // Check if the error is an unauthorized error (401)
        isUnauthorized ||= (code === 401 || type === "UNAUTHORIZED") && !disableAuthHandleFn(formatted);

        // Display error toast if necessary
        if (!disableToastFn(formatted)) {
          const messageKey = data?.messageKey ?? message ?? error?.message ?? "ERRORS.SOMETHING_WENT_WRONG";
          this.toast.error(messageKey, "ERRORS.SUMMARY");
        }
      }
    }

    return isUnauthorized;
  }

  /**
   * Returns a function to check whether toast notifications should be suppressed for the request.
   * @param req - The HTTP request object.
   * @returns A function to determine if toast should be disabled for the request.
   */
  private getDisableToastFn(req: HttpRequest<unknown>) {
    return this.getDisableFn(req, DISABLE_TOAST, "X-Disable-Toast", "X-Disable-Toast-If-Reason");
  }

  /**
   * Returns a function to check whether authorization handling should be suppressed for the request.
   * @param req - The HTTP request object.
   * @returns A function to determine if authorization handling should be disabled.
   */
  private getDisableAuthHandleFn(req: HttpRequest<unknown>) {
    return this.getDisableFn(req, DISABLE_AUTH_HANDLE, "X-Disable-Auth-Handle", "X-Disable-Auth-Handle-If-Reason");
  }

  /**
   * Returns a function that checks whether toast notifications or authorization handling should be suppressed for the given HTTP request.
   * The function considers multiple mechanisms for disabling the behavior:
   *
   * - A custom function provided via the `contextToken` that can return `true` or `false`.
   * - A boolean `true` value directly set via the `contextToken` to always disable the behavior.
   * - A request header `X-Disable-Toast: true` (or `X-Disable-Auth-Handle: true`) to globally disable the behavior.
   * - A request header `X-Disable-Toast-If-Reason: some_reason` (or `X-Disable-Auth-Handle-If-Reason: some_reason`) to disable the behavior only if the response error matches the specified reason.
   *
   * @param req - The HTTP request object.
   * @param contextToken - The context token used to retrieve the custom function or value from the request context.
   * @param headerName - The name of the header that globally disables the behavior (e.g., `X-Disable-Toast`).
   * @param headerByReasonName - The name of the header that disables the behavior only if a specific error reason is present (e.g., `X-Disable-Toast-If-Reason`).
   * @returns A function that checks if the specified behavior (toast notification or authorization handling) should be disabled for the request.
   */
  private getDisableFn(
    req: HttpRequest<unknown>,
    contextToken: HttpContextToken<boolean | ((error: unknown) => boolean | null) | null>,
    headerName: string,
    headerByReasonName: string
  ) {
    const disable = req.context.get(contextToken);
    const disableHeader = req.headers.get(headerName) === "true";
    const disableByReasonHeader = req.headers.get(headerByReasonName);
    return typeof disable === "function"
      ? disable
      : (error: unknown) =>
          disable ??
          (disableByReasonHeader === ((error as Record<string, unknown>)?.["data"] as Record<string, unknown>)?.["reason"] || disableHeader);
  }
}
